// ============================================================================
// Hooks TanStack Query — GRILLE SALARIALE CE (L06, GC-B) : bundle tab GRILLE
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (M-670 / P-CE14, routes/grilleSalariale.ts : garde `canViewSalaryGridCE` =
// role === 'admin' SEUL, director/manager/employee REFUSES). Le SPA consomme le
// bundle assemble ; il ne DECIDE jamais (lecon GA-05). Un 403 serveur devient une
// redirection /error/403, pas un crash.
//
// Le serveur EST RE-FIGE (GC-A merge) — ce module ne fait que CONSOMMER le contrat
// de /api/grille-salariale (GET / = tab GRILLE). Les types MIROITENT les DTO
// serveur a l'identique (server/routes/grilleSalariale.ts GET / + repositories/
// compensation.ts getGridCeEmployeeLines/getPlanningGrid). La query GET / reste
// LECTURE (le shell + le tab grille). GC-C ajoute les 6 MUTATIONS de l'editeur
// (POST/DELETE /classes, load-default, /years, rollover, migrate) — voir la section
// « Mutations editeur » en bas ; chaque ecriture invalide le bundle GRILLE_CE_ROOT_KEY.
// La grille camelCase est adaptee via `adaptGridClasses` (SL-A, partagee avec L05 —
// les champs additifs prevYearRate/description alimentent la rangee « Taux N-1 » +
// les tooltips de SalaryMasterGrid).
//
// [!] GC-C STOP-Q (rapport) : l'editeur edite la grille MASTER (saveSalaryClass ecrit
// grid_scope='master'), mais AUCUN endpoint GET JSON n'expose la master seule —
// `getSalaryGridMaster` (repo) ne sort que par /export (xlsx). GET / renvoie la
// grille resolue par annee : la MASTER tant que l'annee courante n'est PAS figee,
// le SNAPSHOT de l'annee courante APRES migration (M-679). L'editeur consomme donc
// GET / (fidele AUJOURD'HUI — aucune annee figee en prod) ; le jour ou GC-A expose
// GET /master, basculer ici (1 ligne). Escalade au cerveau au merge.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import type {
  PlanningGridClass, PlanningGridMember, PlanningGridPosition, PlanningEchelonChange,
} from './usePlanification';

// ─── Types — MIROIR EXACT du DTO serveur (grilleSalariale.ts GET /) ───────────

/** Role CE (etage 3). Le DTO ne sort admin qu'apres la garde (director => 403). */
export type GestGridCeRole = 'admin' | 'manager' | 'director' | 'employee';

/**
 * Bundle du tab GRILLE (F-657 / M-680) — miroir de la reponse GET /. Les membres
 * positionnes sont GLOBAUX (toute l'entreprise, P-CE13 : lecture remuneration
 * globale, logPiiAccess serveur) ; le client les pivote via buildEmployeeLines.
 */
export interface GrilleCeResponse {
  viewer: { role: GestGridCeRole; email: string };
  /** Annee selectionnee (figees ∪ courante ; hors liste => courante, resolue serveur). */
  year: number;
  /** Annee courante reelle — PIVOT de buildEmployeeLines (passe = annees < currentYear). */
  currentYear: number;
  /** Pivot de projection de la grille (annee figee => taux reels ; master => 2026 projete). */
  gridYear: number;
  /** Annees figees disponibles (decroissantes) — alimente le YearPicker (∪ courante). */
  gridYears: number[];
  /** Grille de l'annee (master si l'annee n'est pas figee) — camelCase, adaptee client. */
  classes: PlanningGridClass[];
  /** Ombrer les inactifs — vrai ssi vue d'une annee PASSEE (selectedYear < currentYear). */
  shadeInactive: boolean;
  /** Nombre de scenarios soumis — pilote le badge rouge de l'onglet Approbations. */
  submittedCount: number;
  /** Membres positionnes GLOBAUX (entree de buildEmployeeLines). */
  members: PlanningGridMember[];
  /** Positions grille passees par email (buildEmployeeLines). */
  pastByEmail: Record<string, PlanningGridPosition[]>;
  /** Historique d'echelon par email (buildEmployeeLines). */
  historyByEmail: Record<string, PlanningEchelonChange[]>;
}

// ─── Query (bundle tab GRILLE) ────────────────────────────────────────────────

const GRILLE_CE_ROOT_KEY = ['gest', 'grille-ce'] as const;
const grilleCeKey = (year: number | null) => [...GRILLE_CE_ROOT_KEY, year ?? 'current'] as const;

/**
 * F-657 : bundle du tab GRILLE (1 requete SPA). `year` optionnel : le SERVEUR
 * resout l'annee (figees ∪ courante ; hors liste => courante — jamais une valeur
 * hors bornes honoree). 403 (non-admin) = etat stable (pas de retry) : la page
 * redirige vers /error/403 (garde client M-670, double la garde serveur).
 */
export function useGrilleCe(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: grilleCeKey(year),
    queryFn: () =>
      api.get<GrilleCeResponse>('/api/grille-salariale', year != null ? { year: String(year) } : undefined),
    enabled,
    retry: false,
  });
}

// ─── Code d'erreur metier (GA-21 : jamais un code brut a l'ecran) ─────────────

/**
 * Code metier VERBATIM renvoye par le serveur, sinon retombe sur un code generique
 * par statut. La banniere mappe ensuite sur `gest:grilleCe.errors.<code>`.
 */
export function grilleCeErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

/**
 * Codes courts deep-linkables des mutations EDITEUR (P-CE16) : `year_exists` /
 * `empty_master` / `notfound` / `forbidden` / `invalid` / `server`. Le serveur emet
 * des codes `GEST_GRILLE_*` (originalBody.code) ; on les mappe sur les codes courts
 * VERBATIM PoC (le bandeau ?error= de la page d'edition les rend, jamais un code brut).
 * Distinct de grilleCeErrorCode (statut) : le rollover renvoie 400 GEST_GRILLE_YEAR_EXISTS,
 * qui doit rester `year_exists` (pas `invalid`) — sinon la page perd la nuance PoC.
 */
export function gridEditErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string') {
    if (code === 'GEST_GRILLE_YEAR_EXISTS') return 'year_exists';
    if (code === 'GEST_GRILLE_EMPTY_MASTER') return 'empty_master';
    if (code === 'GEST_GRILLE_CLASS_NOT_FOUND') return 'notfound';
    if (code === 'GEST_GRILLE_FORBIDDEN') return 'forbidden';
    if (code === 'GEST_GRILLE_INVALID') return 'invalid';
  }
  if (err instanceof ApiForbiddenError) return 'forbidden';
  return 'server';
}

// ─── Mutations editeur (6) — F-663/665, M-675/676/677/679 (GC-C) ──────────────
// Chaque ecriture invalide GRILLE_CE_ROOT_KEY : le serveur re-assemble la grille
// apres persistance (VERBATIM PoC : saveSalaryClassAction fait revalidatePath des 2
// pages). Les cartes de l'editeur restent montees (key = id de classe stable) — leur
// brouillon local survit au refetch (la carte suit son propre `savedSnapshot`, comme
// le PoC). Le SERVEUR est l'AUTORITE des recalculs (M-675) : le client n'envoie que
// le BRUT (rate, rate_2025, aug en %, labels) ; pct_from_prev, aug %/100 et la
// reindexation 1..n sont recalcules serveur (normalizeSavePayload).

/**
 * Payload editeur (M-675) — MIROIR EXACT de SavePayloadSchema serveur
 * (routes/grilleSalariale.ts). `id` = GUID de la classe MASTER a re-editer, ou ''
 * (nouvelle : le serveur slugify(label) -> class_key). `aug_grille_pct` en
 * POURCENTAGE (ex. 2.5) — le serveur le divise par 100. Le serveur ignore/recalcule
 * tout le reste (echelon #, pct_from_prev).
 */
export interface SalaryClassSavePayload {
  id: string;
  label: string;
  description: string | null;
  sort_order: number;
  aug_grille_pct: number | null;
  base_hours_per_week: number;
  weeks_per_year: number;
  echelons: { rate: number; rate_2025: number | null }[];
  niveaux: { label: string; echelon_max: number | null; description: string | null }[];
}

/** F-663 / M-675 : upsert d'une classe MASTER (POST /classes). Toast cote carte. */
export function useSaveSalaryClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SalaryClassSavePayload) =>
      api.post<{ ok: 'saved'; classKey: string }>('/api/grille-salariale/classes', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-663 : soft-delete d'une classe MASTER (DELETE /classes/:id, GUID). Toast. */
export function useDeleteSalaryClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: 'deleted' }>(`/api/grille-salariale/classes/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-663 / M-676 : charge la grille de reference GRILLE 2026 (merge sans perte des
 * customs, serveur). Redirige ?ok=loaded cote page. */
export function useLoadDefaultGrid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: 'loaded' }>('/api/grille-salariale/classes/load-default', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/**
 * F-665 : upsert d'une classe dans la grille FIGEE d'une annee (POST /years).
 * DORMANT en GC-C : le PoC n'a AUCUN ecran qui l'invoque (les snapshots annuels se
 * construisent via rollover/migrate ; le save-par-annee existe serveur, sans UI).
 * Expose pour parite de contrat (le hook = couche de wiring vers GC-A) — a brancher
 * si un editeur d'annee figee arrive (hors PoC).
 */
export function useSaveSalaryClassYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; payload: SalaryClassSavePayload }) =>
      api.post<{ ok: 'saved'; classKey: string }>('/api/grille-salariale/years', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-665 / M-677 : cree la grille N+1 depuis N (POST /years/rollover), idempotent —
 * l'annee cible existante -> 400 GEST_GRILLE_YEAR_EXISTS (gridEditErrorCode). */
export function useRolloverGrid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fromYear: number) =>
      api.post<{ ok: 'rolled'; year: number }>('/api/grille-salariale/years/rollover', {
        from_year: fromYear,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-665 / M-679 : migration initiale idempotente (POST /years/migrate). Master vide
 * -> 400 GEST_GRILLE_EMPTY_MASTER. */
export function useMigrateAnnualGrids() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: 'migrated'; seededRates: number }>('/api/grille-salariale/years/migrate', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}
